/**
 * Gate Deviation Dialog Component
 *
 * Displays a scatter plot showing gate deviations for selected operations.
 * Features:
 * - Color-coded by operation type (arrival/departure/etc)
 * - Different markers for same/opposite direction
 * - Clickable tooltips that can be pinned to the chart
 * - Unit conversion support
 * - Interactive hover and click tooltips
 */

import React, {
  FC,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  type Chart,
  type ChartEvent,
  type ActiveElement,
  type ScriptableScaleContext,
  type Tick,
} from 'chart.js';

import { DialogFooter, Button, Size } from '@blueprintjs/core-modern';
import { Dialog } from 'src/componentsModern';
import { useDataSelectors } from 'src/@operations/reducers';
import { OperationSummary } from 'src/app/gql/__generated__/hooks';
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
import { getOperationTheme, getDistance } from 'src/utils';
import {
  INPUT_RETURN_TYPES,
  UNIT_METER,
  UNIT_KILOMETER,
  UNIT_NAUTICAL_MILE,
  UNIT_MILE,
  UNIT_FOOT,
} from 'src/constants';

import { DeviationTooltip as DeviationTooltipType, TGateValues } from './types';
import { DeviationTooltip, DeviationChartLegend } from './components';
import {
  convertGateIntersectionData,
  getOperationColor,
  isOppositeDirection,
  calculateYAxisConfig,
  calculateXAxisConfig,
  exportChartAsImage,
  getTooltipRenderData,
  getGateXCoordinates,
  convertGateVerticalDistance,
} from './helpers';
import { CHART_CONSTANTS, MARKER_STYLES } from './constants';
import { TVerticalDistanceUnit, TDistanceUnit } from 'src/utils/interfaces';
import { GraphLoading } from '../TrackProfile/TrackProfileDialog.styles';
import {
  StyledDialogBody,
  ChartBodyWrapper,
  ChartContainer,
  ChartArea,
  ChartInner,
  ChartZoomControlsOverlay,
  ChartFooter,
  ZoomHint,
  DialogFooterRow,
  DialogFooterButtonGroup,
} from './GateDeviationDialog.styles';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { meterToFoot } from '@ems/client-design-system';

ChartJS.register(LinearScale, PointElement, LineElement, annotationPlugin, zoomPlugin);

export interface IGateDeviationGraph {
  selectedOperationIds: number[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const GateDeviationDialog: FC<IGateDeviationGraph> = ({
  selectedOperationIds,
  isOpen,
  setIsOpen,
}) => {
  // Get configuration and theme
  const configSelectors = useConfigSelectors();
  const units = configSelectors.getUnits();
  const selectedTrackTheme = configSelectors.getTheme('operations');
  const colorList = getOperationTheme(selectedTrackTheme);

  // Get translations
  const languageSelectors = useLanguageSelectors();
  const {
    screens: {
      operations: { deviationGraph },
    },
    components: {
      dialogs: { closeDialog, gateDeviationHeader },
      ruleDetails: {
        fields: {
          gateWidth: gateWidthLabel,
          floorHeight: floorHeightLabel,
          ceilingHeight: ceilingHeightLabel,
          gateValues: gateValuesLabel,
        },
      },
    },
    fields: {
      operations: {
        gateInterceptsCountOf,
        gateDirectionShort,
        gateAltitudeShort,
        gateDirection,
        gateDirectionSame,
        gateDirectionOpposite,
      },
    },
  } = languageSelectors.getLanguage();

  // Get selected operations data
  const dataSelectors = useDataSelectors();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- getDataInformation() return type is broad
  const { data } = dataSelectors.getDataInformation();
  const operationsMap = data as Map<number, OperationSummary>;
  const selectedOperations: OperationSummary[] = selectedOperationIds
    .map(id => operationsMap?.get(id))
    .filter((operation): operation is OperationSummary => Boolean(operation));
  const gateName = selectedOperations[0]?.gateIntersections?.[0]?.gate?.name || '';

  // State for pinned tooltips (user can click to pin/unpin)
  const [clickedTooltips, setClickedTooltips] = useState<DeviationTooltipType[]>([]);
  // State for temporary tooltip (shown on hover)
  const [hoverTooltip, setHoverTooltip] = useState<DeviationTooltipType | null>(null);
  // State for loading buffer
  const [isLoadBuffer, setIsLoadBuffer] = useState(true);
  // State for gate dimensions values
  const [gateValues, setGateValues] = useState<TGateValues | null>(null);
  // Persist zoom/pan scale limits so hover/click re-renders don't reset the view
  const [scaleLimits, setScaleLimits] = useState<{
    x: { min: number; max: number };
    y: { min: number; max: number };
  } | null>(null);

  // Chart management
  const chartRef = useRef<Chart | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // Track current hover state to prevent unnecessary updates
  const currentHoverRef = useRef<{ datasetIndex: number; dataIndex: number } | null>(null);
  // Skip hover updates while zoom/pan so re-renders don't re-apply options mid-gesture (avoids lag/bounce)
  const zoomOrPanInProgressRef = useRef(false);

  const zoomChart = useCallback((direction: 'in' | 'out') => {
    const chart = chartRef.current;
    if (!chart) {return;}

    if (direction === 'in') {
      chart.zoom(1.1);
    } else {
      chart.zoom(0.9);

    }

    // Persist scale limits after programmatic zoom so hover/rerender doesn't reset the view
    const captureScale = (): void => {
      const xScale = chart.scales?.x;
      const yScale = chart.scales?.y;
      if (xScale && yScale) {
        setScaleLimits({
          x: { min: xScale.min, max: xScale.max },
          y: { min: yScale.min, max: yScale.max },
        });
      }
      setClickedTooltips([]);
    };
    // Zoom plugin may update scales async; capture after it has applied
    setTimeout(captureScale, 0);
  }, []);

  const gate = useMemo(() => {
    if (selectedOperations.length === 0) {
      return null;
    }
    return selectedOperations[0]?.gateIntersections?.[0]?.gate;
  }, [selectedOperations]);

  const conversionSettings = useMemo(() => {
    // Calculate gate width in m to determine if we should override the unit
    const gateWidthInMeters = gate ? getGateXCoordinates(gate, 'meters') : null;
    const gateWidthInFeet: number = meterToFoot(gateWidthInMeters);

    // If unit is m or km AND gate width is less than 1000 feet, use meters
    let convertTo = units.distance;

    // Design system conversion has a hardcoded switch if values are less than 10000 ft/m
    if (
      (units.distance === UNIT_METER || units.distance === UNIT_KILOMETER) &&
      gateWidthInMeters < 10000
    ) {
      convertTo = UNIT_METER as TDistanceUnit;
    }
    if (
      (units.distance === UNIT_FOOT ||
        units.distance === UNIT_MILE ||
        units.distance === UNIT_NAUTICAL_MILE) &&
      gateWidthInFeet < 10000
    ) {
      convertTo = UNIT_FOOT as TDistanceUnit;
    }

    return {
      convertTo,
      convertFrom: UNIT_METER as TDistanceUnit,
      returnValueType: INPUT_RETURN_TYPES.NUMBER,
    };
  }, [units.distance, gate]);

  const conversionSettingsVertical = useMemo(
    () => ({
      convertTo: units.distanceVertical,
      convertFrom: UNIT_FOOT as TVerticalDistanceUnit,
      returnValueType: INPUT_RETURN_TYPES.NUMBER,
    }),
    [units.distanceVertical]
  );

  // Reset hover state when dialog closes or operations change
  useEffect(() => {
    if (!isOpen) {
      currentHoverRef.current = null;
      setHoverTooltip(null);
    }
  }, [isOpen]);

  // Reset zoom state when dialog closes or selected operations change
  useEffect(() => {
    if (!isOpen || selectedOperationIds.length === 0) {
      setScaleLimits(null);
    }
  }, [isOpen, selectedOperationIds]);

  // Trigger chart resize when dialog opens (Chart.js handles this automatically)
  useEffect(() => {
    if (isOpen && chartRef.current) {
      const chart = chartRef.current;
      const timer = setTimeout(() => {
        chart.resize();
      }, CHART_CONSTANTS.CHART_RESIZE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Create the gate values when chart loads
  useEffect(() => {
    if (isOpen) {
      const gateWidth = getDistance(
        getGateXCoordinates(gate, 'meters'),
        conversionSettings,
        2
      ) as number;
      // Gate floor/ceiling altitude are in feet,
      setGateValues({
        ceilingAltitude: convertGateVerticalDistance(
          gate?.geometry?.ceilingAltitude,
          conversionSettingsVertical
        ),
        floorAltitude: convertGateVerticalDistance(
          gate?.geometry?.floorAltitude,
          conversionSettingsVertical
        ),
        gateWidth,
        gateLeft: -gateWidth / 2,
        gateRight: gateWidth / 2,
      });
    }
  }, [isOpen, conversionSettings, conversionSettingsVertical, gate]);

  /**
   * Exports the chart (including pinned tooltips) as a JPG image
   */
  const handleExportChart = async () => {
    if (chartContainerRef.current) {
      try {
        await exportChartAsImage(chartContainerRef.current);
      } catch (error) {
        console.error('Failed to export chart:', error);
      }
    }
  };

  /**
   * Generate chart datasets from selected operations
   * Each operation becomes a dataset with one or more data points (one per gate intersection)
   */
  const chartData = useMemo(() => {
    const datasets = selectedOperations
      .map(operation => {
        const dataPoints = convertGateIntersectionData(
          operation,
          conversionSettings,
          conversionSettingsVertical
        );
        if (dataPoints.length === 0) {
          return null;
        }

        const color = getOperationColor(operation.operationType, colorList);
        const isOpposite = isOppositeDirection(operation);
        return {
          label: operation.acid,
          data: dataPoints,
          backgroundColor: color,
          pointRadius: CHART_CONSTANTS.POINT_RADIUS,
          pointHoverRadius: CHART_CONSTANTS.POINT_RADIUS, // Prevent size change on hover
          pointHoverBorderWidth: 0, // Prevent border width change on hover
          pointStyle: isOpposite ? MARKER_STYLES.OPPOSITE_DIRECTION : MARKER_STYLES.SAME_DIRECTION,
        };
      })
      .filter(Boolean);

    return { datasets };
  }, [selectedOperations, colorList, conversionSettings, conversionSettingsVertical]);

  // Memoize hover handler to prevent infinite re-render loops
  // Only update state if the hovered element actually changed
  const handleHover = useCallback(
    (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (zoomOrPanInProgressRef.current) {
        return;
      }
      if (elements.length === 0) {
        if (currentHoverRef.current !== null) {
          currentHoverRef.current = null;
          setHoverTooltip(null);
        }
        return;
      }

      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const dataIndex = element.index;

      if (
        currentHoverRef.current?.datasetIndex !== datasetIndex ||
        currentHoverRef.current?.dataIndex !== dataIndex
      ) {
        const meta = chart.getDatasetMeta(datasetIndex);
        const chartElement = meta.data[dataIndex];
        currentHoverRef.current = { datasetIndex, dataIndex };
        setHoverTooltip({
          datasetIndex,
          dataIndex,
          x: chartElement.x,
          y: chartElement.y,
        });
      }
    },
    []
  );

  // Memoize click handler to prevent unnecessary re-renders
  const handleClick = useCallback(
    (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length > 0) {
        const element = elements[0];
        const meta = chart.getDatasetMeta(element.datasetIndex);
        const chartElement = meta.data[element.index];

        setClickedTooltips(prev => {
          const existingIndex = prev.findIndex(
            t => t.datasetIndex === element.datasetIndex && t.dataIndex === element.index
          );
          if (existingIndex === -1) {
            return [
              ...prev,
              {
                datasetIndex: element.datasetIndex,
                dataIndex: element.index,
                x: chartElement.x,
                y: chartElement.y,
              },
            ];
          }
          return prev;
        });
      }
    },
    []
  );

  /**
   * Configure chart options including axis ranges and interactions
   * Axis ranges are calculated dynamically based on data with a 20% buffer
   */
  const options = useMemo(() => {
    const isFeet = units.distanceVertical === 'ft';

    // Calculate axis configurations from data (used when no zoom state is persisted)
    const yAxisConfig = calculateYAxisConfig(gateValues, isFeet);
    const xAxisConfig = calculateXAxisConfig(gateValues, isFeet, conversionSettings);
    const xMin = scaleLimits?.x.min ?? xAxisConfig.min;
    const xMax = scaleLimits?.x.max ?? xAxisConfig.max;
    const yMin = scaleLimits?.y.min ?? yAxisConfig.min;
    const yMax = scaleLimits?.y.max ?? yAxisConfig.max;

    const captureScaleAndClearTooltips = ({ chart }: { chart: { scales: { x?: { min: number; max: number }; y?: { min: number; max: number } } } }) => {
      zoomOrPanInProgressRef.current = false;
      setHoverTooltip(null);
      const xScale = chart.scales?.x;
      const yScale = chart.scales?.y;
      if (xScale && yScale) {
        setScaleLimits({
          x: { min: xScale.min, max: xScale.max },
          y: { min: yScale.min, max: yScale.max },
        });
      }
      setClickedTooltips([]);
    };

    const onZoomOrPanStart = (): boolean => {
      zoomOrPanInProgressRef.current = true;
      setHoverTooltip(null);
      return true;
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      // Handle hover to show temporary tooltip
      onHover: handleHover,
      // Handle click to pin/unpin tooltips
      onClick: handleClick,
      interaction: {
        intersect: true, // Only trigger on direct intersection, not just proximity
        mode: 'point' as const, // Only interact with points, not the entire dataset
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          intersect: true, // Only trigger on direct intersection
        },
        annotation: {
          annotations: {
            ...(gateValues && {
              gateBox: {
                type: 'box' as const,
                xMin: gateValues.gateLeft,
                xMax: gateValues.gateRight,
                yMin: gateValues.floorAltitude,
                yMax: gateValues.ceilingAltitude,
                backgroundColor: 'rgba(128, 128, 128, 0.3)',
                borderWidth: 0,
              },
            }),
          },
        },
        zoom: {
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            mode: 'xy' as const,
            onZoomStart: onZoomOrPanStart,
            onZoomComplete: captureScaleAndClearTooltips,
          },
          pan: {
            enabled: true,
            mode: 'xy' as const,
            onPanStart: onZoomOrPanStart,
            onPanComplete: captureScaleAndClearTooltips,
          },
          // Use explicit default axis range so zoom-out always returns to load view
          // ('original' can get stuck after first zoom when we persist scaleLimits)
          limits: {
            x: { min: xAxisConfig.min, max: xAxisConfig.max },
            y: { min: yAxisConfig.min, max: yAxisConfig.max },
          },
        },
      },
      scales: {

        // X-axis: Lateral deviation (centered at 0)
        x: {
          ticks: {
            callback: (val: number | string, index: number, ticks: Tick[]) =>
              index === 0 || index === ticks.length - 1 ? null : val,
          },
          min: xMin,
          max: xMax,
          title: {
            display: true,
            text: `${deviationGraph.xAxisLabel.replace('{unit}', conversionSettings.convertTo)}`,
          },
          grid: {
            color: (context: ScriptableScaleContext) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_COLOR
                : CHART_CONSTANTS.GRID_LINE_COLOR,
            lineWidth: (context: ScriptableScaleContext) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_WIDTH
                : CHART_CONSTANTS.GRID_LINE_WIDTH,
          },
        },
        // Y-axis: Altitude
        y: {
          ticks: {
            callback: (val: number | string, index: number, ticks: Tick[]) =>
              index === 0 || index === ticks.length - 1 ? null : val,
          },
          min: yMin,
          max: yMax,
          title: {
            display: true,
            text: `${deviationGraph.yAxisLabel.replace(
              '{unit}',
              conversionSettingsVertical.convertTo
            )}`,
          },
        },
      },
    };
  }, [
    selectedOperations,
    units,
    conversionSettings,
    handleHover,
    handleClick,
    deviationGraph,
    gateValues,
    scaleLimits,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadBuffer(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Memoize hover tooltip render data to prevent unnecessary recalculations
  const hoverTooltipRenderData = useMemo(() => {
    if (!hoverTooltip) {
      return null;
    }
    return getTooltipRenderData(hoverTooltip, chartData);
  }, [hoverTooltip?.datasetIndex, hoverTooltip?.dataIndex, chartData]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      isCloseButtonShown
      size={Size.LARGE}
      title={`${gateDeviationHeader} ${gateName ? `- ${gateName}` : ''}`}>
      <StyledDialogBody>
        <ChartBodyWrapper>
          <GraphLoading loading={isLoadBuffer} size="l">
            <ChartContainer ref={chartContainerRef}>
              <ChartArea>
                <ChartInner>
                  <Scatter
                    ref={chartRef as React.RefObject<Chart<'scatter', (number | [number, number] | import('chart.js').Point | import('chart.js').BubbleDataPoint)[], unknown> | null>}
                    data={chartData}
                    options={options}
                  />

                  {hoverTooltip && hoverTooltipRenderData && (
                    <DeviationTooltip
                      key={`${hoverTooltip.datasetIndex}-${hoverTooltip.dataIndex}`}
                      tooltip={hoverTooltip}
                      dataPoint={hoverTooltipRenderData.dataPoint}
                      label={hoverTooltipRenderData.label}
                      distanceUnit={units.distanceVertical}
                      intersectionIndex={hoverTooltipRenderData.intersectionIndex}
                      totalIntersections={hoverTooltipRenderData.totalIntersections}
                      translations={{
                        gateInterceptsCountOf,
                        gateDirectionShort,
                        gateAltitudeShort,
                      }}
                      onRemove={() => setHoverTooltip(null)}
                    />
                  )}
                  {clickedTooltips.map(tooltip => {
                    const renderData = getTooltipRenderData(tooltip, chartData);
                    if (!renderData) {
                      return null;
                    }

                    return (
                      <DeviationTooltip
                        key={`clicked-${tooltip.datasetIndex}-${tooltip.dataIndex}`}
                        tooltip={tooltip}
                        dataPoint={renderData.dataPoint}
                        label={renderData.label}
                        distanceUnit={units.distanceVertical}
                        intersectionIndex={renderData.intersectionIndex}
                        totalIntersections={renderData.totalIntersections}
                        translations={{
                          gateInterceptsCountOf,
                          gateDirectionShort,
                          gateAltitudeShort,
                        }}
                        onRemove={() =>
                          setClickedTooltips(prev =>
                            prev.filter(
                              t =>
                                !(t.datasetIndex === tooltip.datasetIndex && t.dataIndex === tooltip.dataIndex)
                            )
                          )
                        }
                      />
                    );
                  })}
                </ChartInner>
                <ChartZoomControlsOverlay>
                  <Button
                    icon="small-plus"
                    variant="outlined"
                    aria-label="Zoom in"
                    onClick={() => zoomChart('in')}
                  />
                  <Button
                    icon="small-minus"
                    variant="outlined"
                    aria-label="Zoom out"
                    onClick={() => zoomChart('out')}
                  />
                </ChartZoomControlsOverlay>
              </ChartArea>
              <ChartFooter>
                <DeviationChartLegend
                  translations={{
                    gateDirection,
                    gateDirectionSame,
                    gateDirectionOpposite,
                    gateWidth: gateWidthLabel,
                    floorHeight: floorHeightLabel,
                    ceilingHeight: ceilingHeightLabel,
                    gateValues: gateValuesLabel,
                  }}
                  gateValues={gateValues}
                  units={{
                    vertical: conversionSettingsVertical.convertTo,
                    horizontal: conversionSettings.convertTo,
                  }}
                />
                <ZoomHint>{deviationGraph.zoomTooltip}</ZoomHint>
              </ChartFooter>
            </ChartContainer>
          </GraphLoading>
        </ChartBodyWrapper>
      </StyledDialogBody>
      <DialogFooter>
        <DialogFooterRow>
          <DialogFooterButtonGroup>
            <Button
              text={deviationGraph.clearTags}
              disabled={clickedTooltips.length === 0}
              onClick={() => setClickedTooltips([])}
            />
            <Button
              text={deviationGraph.resetZoom}
              disabled={scaleLimits === null}
              onClick={() => {
                setClickedTooltips([]);
                setScaleLimits(null);
              }}
            />
          </DialogFooterButtonGroup>
          <DialogFooterButtonGroup>
            <Button
              text={deviationGraph.exportButton}
              onClick={handleExportChart}
              disabled={isLoadBuffer}
            />
            <Button
              intent="primary"
              text={closeDialog}
              onClick={() => {
                setIsOpen(false);
              }}
            />
          </DialogFooterButtonGroup>
        </DialogFooterRow>
      </DialogFooter>
    </Dialog>
  );
};
